import React, { useState, useEffect, useMemo, useCallback } from "react";
import { polygonCentroid, buildSectors, computeBBox } from "@/lib/sectors";
import { snapPoint } from "@/lib/corners";
import { useCanvasView } from "@/hooks/useCanvasView";
import { PolygonLayer, SectorLayer, BrahmasthanMarker, PolygonPoints, MarkerLayer, NorthIndicator, SnapLayer } from "@/components/canvas/Layers";

const CURSOR = { draw: "cursor-crosshair-cad", marker: "cursor-copy", pan: "cursor-grab" };
const SNAP_RADIUS_PX = 14;
const AXIS_TOL_PX = 10;

// SVG-based interactive canvas: fixed plan image + rotating compass grid, polygon & room markers
export default function FloorPlanCanvas({
  image, imageDims, rotation = 0, polygon = [], mode = "draw", sectorCount = 16, showOverlay = true, showDegrees = true,
  onAddPoint, onMovePoint, hoveredSector, onHoverSector, onClickSector,
  markers = [], selectedMarker, onAddMarker, onMoveMarker, onSelectMarker,
  corners = [], snapEnabled = false,
}) {
  const { svgRef, wrapRef, view, clientToImage, zoomBy, panBy } = useCanvasView(imageDims);
  const [dragging, setDragging] = useState(null); // {type:'pan'|'point'|'marker', ...}
  const [hoverSnap, setHoverSnap] = useState(null);

  const w = imageDims?.width || 800;
  const h = imageDims?.height || 600;
  const insideImage = (p) => p.x >= 0 && p.y >= 0 && p.x <= w && p.y <= h;

  const center = useMemo(() => (polygon.length >= 1 ? polygonCentroid(polygon) : null), [polygon]);
  const bbox = useMemo(() => computeBBox(polygon, w, h), [polygon, w, h]);
  const sectors = useMemo(() => {
    if (!center || polygon.length < 3 || !showOverlay) return [];
    return buildSectors(sectorCount, center, polygon, bbox, rotation);
  }, [center, polygon, bbox, sectorCount, rotation, showOverlay]);
  const hasPolygon = polygon.length >= 3;
  const snapActive = snapEnabled && mode === "draw" && !!image;

  // Snap helper: reference = previous point (or neighbour when dragging index idx)
  const applySnap = useCallback((p, bypass, excludeIdx = null) => {
    if (!snapActive || bypass) return { point: p, kind: null, target: null };
    const ref = excludeIdx === null
      ? polygon[polygon.length - 1] || null
      : polygon[(excludeIdx - 1 + polygon.length) % polygon.length] || null;
    return snapPoint(p, corners, SNAP_RADIUS_PX / view.scale, ref && ref !== polygon[excludeIdx] ? ref : null, AXIS_TOL_PX / view.scale);
  }, [snapActive, corners, polygon, view.scale]);

  const handleMouseDown = (e) => {
    if (e.button !== 0) return;
    if (mode === "pan" || e.shiftKey) {
      setDragging({ type: "pan", startX: e.clientX, startY: e.clientY, startTx: view.tx, startTy: view.ty });
    }
  };

  const handleMouseMove = (e) => {
    if (!snapActive || dragging) { if (hoverSnap) setHoverSnap(null); return; }
    const p = clientToImage(e.clientX, e.clientY);
    const s = applySnap(p, e.altKey);
    setHoverSnap(s.kind ? s : null);
  };

  const handleClick = (e) => {
    if (dragging || e.shiftKey) return;
    const ds = e.target?.dataset || {};
    if (ds.markerId !== undefined || ds.pointIdx !== undefined || ds.sectorIdx !== undefined) return;
    if (mode !== "draw" && mode !== "marker") return;
    const raw = clientToImage(e.clientX, e.clientY);
    if (!insideImage(raw)) return;
    if (mode === "marker") { onAddMarker?.(raw); return; }
    onAddPoint?.(applySnap(raw, e.altKey).point);
  };

  // Global mouse listeners while dragging
  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      if (dragging.type === "pan") {
        panBy(dragging.startTx + (e.clientX - dragging.startX), dragging.startTy + (e.clientY - dragging.startY));
      } else if (dragging.type === "point") {
        const s = applySnap(clientToImage(e.clientX, e.clientY), e.altKey, dragging.idx);
        setHoverSnap(s.kind ? s : null);
        onMovePoint?.(dragging.idx, s.point);
      } else if (dragging.type === "marker") {
        onMoveMarker?.(dragging.id, clientToImage(e.clientX, e.clientY));
      }
    };
    const onUp = () => { setDragging(null); setHoverSnap(null); };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [dragging, clientToImage, panBy, onMovePoint, onMoveMarker, applySnap]);

  return (
    <div ref={wrapRef} className="relative flex-1 canvas-dot-grid overflow-hidden rounded-xl border border-slate-800 min-h-[400px] noise-overlay" data-testid="vastu-canvas-container">
      <svg ref={svgRef} width="100%" height="100%" className={CURSOR[mode] || CURSOR.pan}
        onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseLeave={() => setHoverSnap(null)} onClick={handleClick} style={{ touchAction: "none" }}>
        <g transform={`translate(${view.tx} ${view.ty}) scale(${view.scale})`}>
          {image && <image href={image.src} width={w} height={h} preserveAspectRatio="none" />}
          <PolygonLayer polygon={polygon} scale={view.scale} />
          <SectorLayer sectors={sectors} center={center} sectorCount={sectorCount} hoveredSector={hoveredSector}
            mode={mode} scale={view.scale} rotation={rotation} showDegrees={showDegrees} onHoverSector={onHoverSector} onClickSector={onClickSector} />
          {center && hasPolygon && <BrahmasthanMarker center={center} scale={view.scale} />}
          {snapActive && <SnapLayer corners={corners} snap={hoverSnap} scale={view.scale} />}
          <PolygonPoints polygon={polygon} scale={view.scale} onStartDrag={(idx) => setDragging({ type: "point", idx })} />
          <MarkerLayer markers={markers} center={hasPolygon ? center : null} rotation={rotation} selectedMarker={selectedMarker}
            scale={view.scale} onStartDrag={(id) => setDragging({ type: "marker", id })} onSelect={onSelectMarker} />
        </g>
        <NorthIndicator rotation={rotation} />
      </svg>

      {snapActive && (
        <div className="absolute top-4 right-4 px-2.5 py-1 rounded-md bg-slate-900/90 border border-sky-500/40 font-mono text-[10px] text-sky-300 backdrop-blur-md" data-testid="snap-status">
          SNAP · {corners.length} corners · hold Alt to bypass
        </div>
      )}

      <div className="absolute bottom-4 right-4 flex flex-col gap-1 bg-slate-900/90 border border-slate-800 rounded-lg p-1 backdrop-blur-md">
        <button data-testid="zoom-in-button" className="w-8 h-8 text-slate-300 hover:bg-slate-800 rounded flex items-center justify-center" onClick={() => zoomBy(1.2)}>+</button>
        <div className="font-mono text-[10px] text-slate-500 text-center py-1">{Math.round(view.scale * 100)}%</div>
        <button data-testid="zoom-out-button" className="w-8 h-8 text-slate-300 hover:bg-slate-800 rounded flex items-center justify-center" onClick={() => zoomBy(1 / 1.2)}>−</button>
      </div>

      {!image && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-center max-w-md px-6">
            <div className="font-display text-2xl text-slate-400 mb-2">No floor plan loaded</div>
            <div className="text-sm text-slate-500">Upload a plan (JPG, PNG, SVG, PDF) or load a sample to begin</div>
          </div>
        </div>
      )}
    </div>
  );
}
